import { Component, Input, OnDestroy, OnInit, Optional, ViewChild } from "@angular/core";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { MatMenuTrigger } from "@angular/material/menu";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AppThemeColors, LoggingService, SnackBarService } from "src/app/core";
import { VariantSkuFavoritesList, VisionboardItem } from "../../../models";
import { VisionboardFavoritesServiceBase } from "../../../services";
import { VisionboardItemFavoriteButtonMenuService } from "../../services";

@Component({
    selector: "app-visionboard-item-favorite-button-menu",
    templateUrl: "./visionboard-item-favorite-button-menu.component.html",
    styleUrls: ["./visionboard-item-favorite-button-menu.component.scss"],
    standalone: false
})
export class VisionboardItemFavoriteButtonMenuComponent implements OnInit, OnDestroy {
  private _destroyed$: Subject<void> = new Subject<void>();

  private _hasFavoritesSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  hasFavorites$: Observable<boolean> = this._hasFavoritesSubject$.asObservable();

  private _tooltipMsgSubject$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  tooltipMsg$: Observable<string> = this._tooltipMsgSubject$.asObservable();

  @Input() item: VisionboardItem;
  @Input() favoritesList: VariantSkuFavoritesList;
  @Input() source: string;
  @Input() isMenuOpen$: BehaviorSubject<boolean>;

  favoritesLists: VariantSkuFavoritesList[];
  isVisible: boolean = false;
  buttonColor: AppThemeColors;

  @ViewChild("favoriteListMenuTrigger", { read: MatMenuTrigger }) favoriteListMenuTrigger: MatMenuTrigger;

  constructor(
    private _visionboardFavoritesService: VisionboardFavoritesServiceBase,
    private _snackBarService: SnackBarService,
    private _loggingService: LoggingService,
    @Optional() private _visionboardItemFavoriteButtonMenuService: VisionboardItemFavoriteButtonMenuService,
  ) {}

  ngOnInit(): void {
    if (!this._visionboardFavoritesService.isFavoritesSupported()) {
      return;
    } else {
      this.isVisible = true;
    }

    if (this.favoritesList) {
      this.favoritesLists = [this.favoritesList];
      this.setHasFavorites();
    } else {
      this._visionboardFavoritesService.favoritesLists$.pipe(takeUntil(this._destroyed$)).subscribe({
        next: favoritesLists => {
          this.favoritesLists = favoritesLists;

          this.isVisible = !!(this.favoritesLists && this.favoritesLists.length);

          this.setHasFavorites();
        },
      });
    }

    this._visionboardFavoritesService.favoritesUpdated$.pipe(takeUntil(this._destroyed$)).subscribe({
      next: updatedItems => {
        for (let updatedItem of updatedItems) {
          if (this.item.variantSkuId === updatedItem.variantSkuId) {
            this.item = updatedItem;
            this.setHasFavorites();
            break;
          }
        }
      },
    });

    this.buttonColor = this._visionboardFavoritesService.favoriteItemColor;
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  onFavoriteClick($event: MouseEvent): void {
    $event.stopPropagation();

    if (!this.favoritesLists || !this.favoritesLists.length) {
      return;
    }

    if (this.favoritesLists.length > 1) {
      this.isMenuOpen$?.next(true);

      this.favoriteListMenuTrigger.openMenu();
    } else {
      const firstFavoritesListId = this.favoritesLists[0].listId;
      if (this.item.favoritesListIds?.length) {
        this._visionboardItemFavoriteButtonMenuService?.unfavoritedClick(this.item);
        this._removeSkuFromFavoritesList(firstFavoritesListId);
      } else {
        this._visionboardItemFavoriteButtonMenuService?.favoritedClick(this.item);
        this._addSkuToFavoritesList(firstFavoritesListId);
      }
      this.setHasFavorites();
    }
  }

  setHasFavorites(): void {
    const favoritesListsCount = this.item.favoritesListIds?.length;

    this._hasFavoritesSubject$.next(favoritesListsCount > 0);

    if (favoritesListsCount === 0 || !this.favoritesLists) {
      this._tooltipMsgSubject$.next(this._visionboardFavoritesService.favoriteItemText);
    } else if (this.favoritesLists.length === 1) {
      this._tooltipMsgSubject$.next(this._visionboardFavoritesService.unfavoriteItemText);
    } else {
      this._tooltipMsgSubject$.next(this._visionboardFavoritesService.favoriteMenuText);
    }
  }

  isFavoritesListChecked(list: VariantSkuFavoritesList): boolean {
    if (!this.item.favoritesListIds) {
      return false;
    }

    return this.item.favoritesListIds.indexOf(list.listId) > -1;
  }

  onFavoritesCheckboxChange(event: MatCheckboxChange, list: VariantSkuFavoritesList): void {
    if (event.checked) {
      this._addSkuToFavoritesList(list.listId);
    } else {
      this._removeSkuFromFavoritesList(list.listId);
    }
  }

  onFavoritesListMenuClose(): void {
    this.isMenuOpen$?.next(false);
  }

  private _addSkuToFavoritesList(listId: number): void {
    this.item.favoritesListIds.push(listId);
    this.setHasFavorites();

    this._visionboardFavoritesService.addSkuToFavoritesList$(this.item, listId).subscribe({
      next: result => {
        if (result) {
          this._loggingService.logRemoteInfo(
            "Visionboard Favorite Toggle",
            "VisionboardItemFavoriteButtonMenuComponent._addSkuToFavoritesList",
            {
              Workflow: "VisionBoard Interaction",
              WorkflowStep: "Favorite Item",
              WorkflowSource: this.source,
              VariantSkuId: `${this.item.variantSkuId}`,
              FavoritesListId: `${listId}`,
            },
          );
        }
      },
      error: () => {
        this._snackBarService.show({
          message: "Favoriting item failed.  Please try again.",
          action: "Hide",
        });

        this.item.favoritesListIds.splice(this.item.favoritesListIds.indexOf(listId), 1);
        this.setHasFavorites();
      },
    });
  }

  private _removeSkuFromFavoritesList(listId: number): void {
    this.item.favoritesListIds.splice(this.item.favoritesListIds.indexOf(listId), 1);
    this.setHasFavorites();

    this._visionboardFavoritesService.removeSkuFromFavoritesList$(this.item, listId).subscribe({
      next: result => {
        if (result) {
          this._loggingService.logRemoteInfo(
            "Visionboard Favorite Toggle",
            "VisionboardItemFavoriteButtonMenuComponent._removeSkuFromFavoritesList",
            {
              Workflow: "VisionBoard Interaction",
              WorkflowStep: "Unfavorite Item",
              WorkflowSource: this.source,
              VariantSkuId: `${this.item.variantSkuId}`,
              FavoritesListId: `${listId}`,
            },
          );

          this._visionboardItemFavoriteButtonMenuService?.afterUnfavorite(this.item);
        }
      },
      error: () => {
        this._snackBarService.show({
          message: "Unfavoriting item failed.  Please try again.",
          action: "Hide",
        });

        this._visionboardItemFavoriteButtonMenuService?.unfavoriteError(this.item);
        this.item.favoritesListIds.push(listId);
        this.setHasFavorites();
      },
    });
  }
}
